import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/admin-layout';
import { Card, Table, Th, Td, Button, Input, Badge, Alert } from '../components/ui';
import { getDraftHonorList, getPeriodeHonorDetail } from '../features/honorarium/api';
import { updateDraftHonorTotalJp } from '../features/honorarium/api';
import type { PeriodeHonor } from '@app/shared';

type DraftHonorRow = {
  id: number;
  pegawai_id: number;
  periode_honor_id: number;
  total_jp: number;
  jumlah_hadir: number;
  honor_kehadiran: number;
  honor_jam_mengajar: number;
  honor_jabatan: number;
  honor_wali_kelas: number;
  total_insentif: number;
  total_honor: number;
  status: 'draft' | 'final';
  nama_pegawai?: string;
  nip_nik?: string;
};

export function DraftHonorTotalJpPage() {
  const { periodeId } = useParams<{ periodeId: string }>();
  const navigate = useNavigate();
  const [periode, setPeriode] = useState<PeriodeHonor | null>(null);
  const [rows, setRows] = useState<DraftHonorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!periodeId) return;
    loadData();
  }, [periodeId]);

  const loadData = async () => {
    if (!periodeId) return;
    setLoading(true);
    setError(null);
    try {
      const [periodeData, draftData] = await Promise.all([
        getPeriodeHonorDetail(Number(periodeId)),
        getDraftHonorList(Number(periodeId)),
      ]);
      setPeriode(periodeData);
      setRows(draftData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row: DraftHonorRow) => {
    setEditingId(row.pegawai_id);
    setEditValue(String(row.total_jp));
    setSuccess(null);
    setError(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSave = async (pegawaiId: number) => {
    if (!periodeId) return;
    const totalJp = Number(editValue);
    if (!Number.isInteger(totalJp) || totalJp < 0) {
      setError('Total JP harus bilangan bulat non-negatif');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateDraftHonorTotalJp(Number(periodeId), pegawaiId, totalJp);
      setSuccess('Total JP berhasil diperbarui');
      setEditingId(null);
      setEditValue('');
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memperbarui Total JP');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = periode?.status === 'draft';

  const formatRupiah = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <AdminLayout title="Input Total JP - Honorarium">
      <div className="space-y-4">
        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Input Total JP per Pegawai</h1>
            {periode && (
              <p className="text-gray-600 mt-1">
                Periode: {periode.nama} ({periode.kode_periode}) -{' '}
                <Badge
                  color={
                    periode.status === 'draft'
                      ? 'gray'
                      : periode.status === 'diproses'
                        ? 'blue'
                        : 'red'
                  }
                >
                  {periode.status}
                </Badge>
              </p>
            )}
          </div>
          <Button variant="secondary" onClick={() => navigate('/honorarium')}>
            Kembali
          </Button>
        </div>

        {!canEdit && (
          <Alert type="info">
            Periode honor tidak berstatus Draft. Total JP tidak dapat diubah.
          </Alert>
        )}

        <Card>
          {loading ? (
            <div className="p-8 text-center text-gray-500">Memuat data...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Tidak ada draft honor. Silakan generate draft honor terlebih dahulu.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>No</Th>
                    <Th>NIP/NIK</Th>
                    <Th>Nama Pegawai</Th>
                    <Th>Total JP</Th>
                    <Th>Honor JP</Th>
                    <Th>Honor Kehadiran</Th>
                    <Th>Total Honor</Th>
                    <Th>Aksi</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const isEditing = editingId === row.pegawai_id;
                    return (
                      <tr key={row.id}>
                        <Td>{idx + 1}</Td>
                        <Td>{row.nip_nik || '-'}</Td>
                        <Td>{row.nama_pegawai || '-'}</Td>
                        <Td>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-24"
                              disabled={saving}
                            />
                          ) : (
                            <span className="font-semibold">{row.total_jp} JP</span>
                          )}
                        </Td>
                        <Td>{formatRupiah(row.honor_jam_mengajar)}</Td>
                        <Td>{formatRupiah(row.honor_kehadiran)}</Td>
                        <Td className="font-semibold">{formatRupiah(row.total_honor)}</Td>
                        <Td>
                          {isEditing ? (
                            <div className="flex gap-2">
                              <Button onClick={() => handleSave(row.pegawai_id)} disabled={saving}>
                                {saving ? 'Menyimpan...' : 'Simpan'}
                              </Button>
                              <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                                Batal
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              onClick={() => handleEdit(row)}
                              disabled={!canEdit}
                            >
                              Edit
                            </Button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        <div className="text-sm text-gray-600">
          <p>
            <strong>Catatan:</strong>
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Total JP adalah input manual Admin untuk setiap pegawai.</li>
            <li>Honor JP dihitung otomatis: Total JP × Tarif Honor per JP.</li>
            <li>Total JP hanya dapat diubah saat Periode Honor berstatus Draft.</li>
            <li>Setelah perubahan, Total Honor akan diperbarui secara otomatis.</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
